import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, X, Search, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', description: '', price: '', old_price: '', stock: '', category: '',
  images: '', featured: false, is_new: false, brand: '', ean: '', specifications: '',
};

const toForm = (p) => ({
  name: p.name || '', description: p.description || '', price: String(p.price ?? ''),
  old_price: p.old_price != null ? String(p.old_price) : '', stock: String(p.stock ?? 0),
  category: p.category || '', images: (p.images || []).join('\n'), featured: !!p.featured,
  is_new: !!p.is_new, brand: p.brand || '', ean: p.ean || '',
  specifications: Object.entries(p.specifications || {}).map(([k, v]) => `${k}: ${v}`).join('\n'),
});

const parseSpecs = (text) => {
  const specs = {};
  text.split('\n').forEach((line) => {
    const idx = line.indexOf(':');
    if (idx > 0) specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  });
  return Object.keys(specs).length ? specs : null;
};

const ProductForm = ({ form, setForm, categories, editing, onSubmit, onCancel }) => {
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <form onSubmit={onSubmit} className="mb-6 p-4 bg-surface rounded-sm space-y-3" data-testid="product-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{editing ? 'Modifier le produit' : 'Nouveau produit'}</h3>
        <button type="button" onClick={onCancel}><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium mb-1">Nom *</label>
          <input type="text" required {...f('name')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-name-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Marque</label>
          <input type="text" {...f('brand')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-brand-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Catégorie *</label>
          <select required {...f('category')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-category-select">
            <option value="">Sélectionner...</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">EAN (8-13 chiffres)</label>
          <input type="text" pattern="\d{8,13}" {...f('ean')} className="w-full px-3 py-2 border border-border rounded-sm font-mono" placeholder="3270587027496" data-testid="product-ean-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Stock *</label>
          <input type="number" min="0" required {...f('stock')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-stock-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Prix de vente TTC (€) *</label>
          <input type="number" step="0.01" min="0" required {...f('price')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-price-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Prix barré « avant promo » (€)</label>
          <input type="number" step="0.01" min="0" {...f('old_price')} className="w-full px-3 py-2 border border-border rounded-sm" placeholder="Laisser vide si pas de promo" data-testid="product-old-price-input" />
        </div>
        <div className="flex items-end gap-4 pb-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-brand-orange" data-testid="product-featured-checkbox" />
            Top vente
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="accent-brand-orange" data-testid="product-new-checkbox" />
            Nouveau
          </label>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Description *</label>
        <textarea required rows="3" {...f('description')} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="product-description-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Images (une URL par ligne, la 1ère est la principale) *</label>
          <textarea required rows="3" {...f('images')} className="w-full px-3 py-2 border border-border rounded-sm font-mono text-xs" placeholder={'https://...\nhttps://...'} data-testid="product-images-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Caractéristiques (une par ligne : Nom: valeur)</label>
          <textarea rows="3" {...f('specifications')} className="w-full px-3 py-2 border border-border rounded-sm text-xs" placeholder={'Puissance: 900W\nGarantie: 2 ans\nPoids: 2,1 kg'} data-testid="product-specs-input" />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary px-4 py-2 rounded-sm font-medium" data-testid="submit-product-button">{editing ? 'Enregistrer' : 'Ajouter'}</button>
        <button type="button" onClick={onCancel} className="btn-secondary px-4 py-2 rounded-sm font-medium">Annuler</button>
      </div>
    </form>
  );
};

const QuickEditRow = ({ product, onSaved }) => {
  const [stock, setStock] = useState(String(product.stock));
  const [price, setPrice] = useState(String(product.price));
  const [saving, setSaving] = useState(false);
  const dirty = stock !== String(product.stock) || price !== String(product.price);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { stock: parseInt(stock, 10), price: parseFloat(price) }, { withCredentials: true });
      toast.success('Stock / prix mis à jour');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 text-xs mb-2">
      <label className="flex items-center gap-1">Stock
        <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)}
          className={`w-16 px-2 py-1 border rounded-sm ${parseInt(stock, 10) <= 0 ? 'border-destructive text-destructive' : 'border-border'}`}
          data-testid={`quick-stock-${product.id}`} />
      </label>
      <label className="flex items-center gap-1">Prix
        <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
          className="w-20 px-2 py-1 border border-border rounded-sm" data-testid={`quick-price-${product.id}`} />€
      </label>
      {dirty && (
        <button onClick={save} disabled={saving} className="ml-auto bg-brand-orange text-white px-2 py-1 rounded-sm flex items-center gap-1 disabled:opacity-50" data-testid={`quick-save-${product.id}`}>
          <Save className="w-3 h-3" /> OK
        </button>
      )}
    </div>
  );
};

export const AdminProducts = ({ products, categories, refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('all');

  const reset = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };
  const startEdit = (p) => { setEditing(p); setForm(toForm(p)); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.ean && !/^\d{8,13}$/.test(form.ean)) { toast.error("L'EAN doit contenir entre 8 et 13 chiffres"); return; }
    const images = form.images.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!images.length) { toast.error('Au moins une image est requise'); return; }
    const oldPrice = form.old_price ? parseFloat(form.old_price) : null;
    if (oldPrice != null && oldPrice <= parseFloat(form.price)) { toast.error('Le prix barré doit être supérieur au prix de vente'); return; }
    const payload = {
      name: form.name, description: form.description, category: form.category, images,
      price: parseFloat(form.price), stock: parseInt(form.stock, 10), old_price: oldPrice,
      featured: form.featured, is_new: form.is_new, brand: form.brand || null, ean: form.ean || null,
      specifications: parseSpecs(form.specifications),
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.id}`, payload, { withCredentials: true });
        toast.success('Produit modifié');
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success('Produit ajouté');
      }
      reset(); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try { await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true }); toast.success('Produit supprimé'); refresh(); }
    catch { toast.error('Erreur'); }
  };

  const q = search.trim().toLowerCase();
  const filtered = products.filter((p) =>
    (!q || p.name.toLowerCase().includes(q) || (p.ean || '').includes(q) || (p.brand || '').toLowerCase().includes(q)) &&
    (!catFilter || p.category === catFilter) &&
    (stockFilter === 'all' || (stockFilter === 'out' && p.stock <= 0) || (stockFilter === 'low' && p.stock > 0 && p.stock <= 5) || (stockFilter === 'promo' && p.old_price))
  );

  return (
    <div className="bg-white rounded-sm border border-border p-6">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>Gestion des produits</h2>
        <button onClick={() => { reset(); setForm({ ...EMPTY, category: categories[0]?.id || '' }); setShowForm(true); }}
          className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2" data-testid="add-product-button">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {showForm && <ProductForm form={form} setForm={setForm} categories={categories} editing={editing} onSubmit={submit} onCancel={reset} />}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, marque ou EAN…"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-sm text-sm" data-testid="product-search-input" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-3 py-2 border border-border rounded-sm text-sm" data-testid="product-category-filter">
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="px-3 py-2 border border-border rounded-sm text-sm" data-testid="product-stock-filter">
          <option value="all">Tous les stocks</option>
          <option value="out">En rupture</option>
          <option value="low">Stock faible (≤ 5)</option>
          <option value="promo">En promotion</option>
        </select>
      </div>
      <p className="text-xs text-muted-foreground mb-3" data-testid="product-count">{filtered.length} produit(s)</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((product) => (
          <div key={product.id} className={`border rounded-sm p-3 ${product.stock <= 0 ? 'border-destructive/50 bg-red-50/30' : 'border-border'}`} data-testid={`admin-product-${product.id}`}>
            <div className="relative">
              <img src={product.images[0]} alt={product.name} className="w-full h-28 object-cover rounded-sm mb-2" />
              <div className="absolute top-1 left-1 flex gap-1">
                {product.old_price && <span className="bg-destructive text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">PROMO</span>}
                {product.is_new && <span className="bg-brand-orange text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">NOUVEAU</span>}
                {product.stock <= 0 && <span className="bg-gray-800 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">RUPTURE</span>}
              </div>
            </div>
            <h3 className="font-semibold mb-1 text-sm line-clamp-2">{product.name}</h3>
            <div className="text-xs text-muted-foreground space-y-0.5 mb-2">
              <p>{product.brand || '—'} {product.ean && <>• EAN <span className="font-mono">{product.ean}</span></>}</p>
              {product.old_price && <p>Avant promo : <span className="line-through">{product.old_price.toFixed(2)} €</span></p>}
            </div>
            <QuickEditRow key={`${product.id}-${product.stock}-${product.price}`} product={product} onSaved={refresh} />
            <div className="flex gap-2">
              <button onClick={() => startEdit(product)} className="flex-1 text-xs flex items-center justify-center gap-1 py-1.5 border border-border rounded-sm hover:bg-surface" data-testid={`edit-product-${product.id}`}>
                <Pencil className="w-3 h-3" /> Modifier
              </button>
              <button onClick={() => remove(product.id)} className="px-2 py-1.5 border border-border rounded-sm hover:border-destructive hover:text-destructive" data-testid={`delete-product-${product.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
